import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebaseConfig"; 
import { sanitizeDescriptionForLogs } from "./logSanitizer";

/**
 * 💾 Saves lead data to Firestore
 * @param {Object} leadData - Lead information to save
 * @returns {Promise<boolean>} - Returns true if successful
 */
export const saveLeadToFirestore = async (leadData) => {
    return await handleFirestoreOperation(async () => {
      const leadRef = doc(db, "zedipass_leads", leadData.email); // Using email as ID
      await setDoc(leadRef, leadData);
      return true;
    }, `Saving lead: ${leadData.email}`);
};
  
/**
 * 🔄 Handles Firestore operations with structured logging.
 * @param {Function} operation - The async Firestore function to execute.
 * @param {string} description - A description for logging purposes.
 * @returns {Promise<any>} - The result of the Firestore operation or false on failure.
 */
export const handleFirestoreOperation = async (operation, description) => {
    try {
        const safeDescription = sanitizeDescriptionForLogs(description);
        console.log(`⏳ Firestore Operation: ${safeDescription}...`);
        const result = await operation();
        console.log(`✅ Success: ${safeDescription}`);
        return result;
    } catch (error) {
        const safeDescription = sanitizeDescriptionForLogs(description);
        console.error(`❌ Firestore Error: ${safeDescription} -`, error.message);
        return false;
    }
};

/**
 * 📤 Saves or updates user data in Firestore.
 * @param {string} userId - The authenticated user's UID.
 * @param {Object} userData - The user data to save.
 * @returns {Promise<boolean>} - Returns true if the operation is successful.
 */
export const saveUserToFirestore = async (userId, userData) => {
    if (!userId || typeof userId !== "string") {
        console.error("❌ Firestore Error: Invalid or missing User ID.");
        return false;
    }
    if (!userData || typeof userData !== "object") {
        console.error("❌ Firestore Error: Invalid user data provided.");
        return false;
    }

    return await handleFirestoreOperation(async () => {
        const userRef = doc(db, "zedipass_users", userId);
        await setDoc(userRef, { 
            ...userData, 
            updatedAt: new Date().toISOString(),
        }, { merge: true });
        return true;
    }, `Saving user profile: ${userId}`);
};


/**
 * 📥 Retrieves user data from Firestore.
 * @param {string} userId - The authenticated user's UID.
 * @returns {Promise<Object|null>} - Returns user data or `null` if not found.
 */
export const getUserFromFirestore = async (userId) => {
    if (!userId || typeof userId !== "string") {
        console.error("❌ Firestore Error: Invalid or missing User ID.");
        return null;
    }

    return await handleFirestoreOperation(async () => {
        const userRef = doc(db, "zedipass_users", userId);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
            console.warn(`⚠️ Firestore Warning: No profile found for user ${userId}`);
            return null;
        }
        console.log(`📄 User data retrieved: ${userId}`);
        return userSnap.data();
    }, `Fetching user profile: ${userId}`);
};

/**
 * 🔄 Updates user data in Firestore.
 * @param {string} userId - The authenticated user's UID.
 * @param {Object} updateData - The fields to update.
 * @returns {Promise<boolean>} - Returns `true` if the update is successful.
 */
export const updateUserData = async (userId, updateData) => {
    if (!userId || typeof userId !== "string") {
        console.error("❌ Firestore Error: Invalid or missing User ID.");
        return false;
    }
    if (!updateData || typeof updateData !== "object") {
        console.error("❌ Firestore Error: Invalid update data.");
        return false;
    }

    return await handleFirestoreOperation(async () => {
        const userRef = doc(db, "zedipass_users", userId);
        await updateDoc(userRef, updateData);
        console.log(`🔄 User profile updated: ${userId}`);
        return true;
    }, `Updating user profile: ${userId}`);
};

/**
 * 🎯 Formats Firestore user data for the frontend.
 * @param {Object} userDoc - The Firestore user document snapshot.
 * @returns {Object|null} - Formatted user data or `null` if no document exists.
 */
export const formatUserData = (userDoc) => {
    if (!userDoc || !userDoc.exists()) return null;
    const data = userDoc.data();
    return {
        id: userDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || null,
        updatedAt: data.updatedAt?.toDate() || null,
    };
};
export {};
